import React from 'react';
import { StockDataPoint, MetricKey } from '../types/StockData';

interface DataSummarySectionProps {
  data: StockDataPoint[];
  selectedDataPoint: StockDataPoint | null;
  selectedMetric: MetricKey;
  getOptionsMetric: (point: StockDataPoint, metricKey: MetricKey) => number;
}

const DataSummarySection: React.FC<DataSummarySectionProps> = ({
  data,
  selectedDataPoint,
  selectedMetric,
  getOptionsMetric
}) => {
  const formatTimestamp = (timestamp: string) => {
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    return new Date(utcTimestamp).toLocaleString('en-US', { timeZone: 'America/New_York' });
  };

  const formatTime = (timestamp: string) => {
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    return new Date(utcTimestamp).toLocaleTimeString('en-US', { timeZone: 'America/New_York' });
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-200">
      {/* Selected Time */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 uppercase tracking-wide">Selected Time:</span>
        <span className="text-sm font-medium text-gray-700">
          {selectedDataPoint ? formatTime(selectedDataPoint.timestamp) : 'Latest'}
        </span>
      </div>
      
      {/* Current Price */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 uppercase tracking-wide">Price:</span>
        <span className="text-sm font-semibold text-blue-600">
          ${selectedDataPoint?.price.toFixed(2) || data[data.length - 1]?.price.toFixed(2)}
        </span>
      </div>
      
      {/* Latest Metric */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 uppercase tracking-wide">{selectedMetric}:</span>
        <span className="text-sm font-semibold text-green-600">
          {(selectedDataPoint ? getOptionsMetric(selectedDataPoint, selectedMetric) : getOptionsMetric(data[data.length - 1], selectedMetric))?.toLocaleString()}
        </span>
      </div>
      
      {/* LDPM */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 uppercase tracking-wide">LDPM:</span>
        <span className="text-sm font-semibold text-purple-600">
          {(() => {
            const ldpmValue = selectedDataPoint?.ldpm?.ldpm ?? data[data.length - 1]?.ldpm?.ldpm;
            return typeof ldpmValue === 'number' ? ldpmValue.toFixed(2) : 'N/A';
          })()}
        </span>
      </div>
      
      {/* Liquidity */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 uppercase tracking-wide">Liquidity:</span>
        <span className={`text-sm font-semibold ${
          (selectedDataPoint?.ldpm?.isLiquidityGood || data[data.length - 1]?.ldpm?.isLiquidityGood) 
            ? 'text-green-600' 
            : 'text-red-600'
        }`}>
          {selectedDataPoint?.ldpm?.liquidityAssessment || data[data.length - 1]?.ldpm?.liquidityAssessment || 'N/A'}
        </span>
      </div>
    </div>
  );
};

export default DataSummarySection;
